/**
 * @fileoverview AI Tool Calling Types
 * @description Type definitions for C.A.L.I AI agent tool calling capabilities
 * @module @flowstate/types/ai-tools
 * @version 1.0.0
 * 
 * WHITE-LABEL READY: These types are designed for use across FlowState, TradieGate,
 * and future applications. No hardcoded branding.
 */

import { z } from 'zod';

// =============================================================================
// TOOL RESULT TYPES
// =============================================================================

/**
 * Standard result wrapper for all tool executions
 */
export interface ToolResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Tool execution context passed to all tools
 */
export interface ToolContext {
  userId: string;
  userRole?: string;
  organizationId?: string;
  timezone?: string;
}

// =============================================================================
// TASK TOOL SCHEMAS (Zod for runtime validation)
// =============================================================================

/**
 * Schema for creating a task
 */
export const CreateTaskToolSchema = z.object({
  title: z.string().min(1).max(500).describe('The title of the task'),
  description: z.string().max(5000).optional().describe('Detailed description of the task'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium').describe('Task priority level'),
  due_date: z.string().optional().describe('Due date in ISO 8601 format (YYYY-MM-DD)'),
  category_id: z.string().uuid().optional().describe('Category ID to organize the task'),
  assigned_to: z.string().uuid().optional().describe('User ID to assign the task to'),
  parent_id: z.string().uuid().optional().describe('Parent task ID if this is a subtask'),
  client_id: z.string().uuid().optional().describe('Client ID to associate with the task'),
});

export type CreateTaskToolInput = z.infer<typeof CreateTaskToolSchema>;

/**
 * Schema for searching tasks
 */
export const SearchTasksToolSchema = z.object({
  query: z.string().optional().describe('Search text to match in title or description'),
  status: z.enum(['todo', 'in_progress', 'completed']).optional().describe('Filter by status'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).optional().describe('Filter by priority'),
  assigned_to: z.string().uuid().optional().describe('Filter by assigned user ID'),
  due_before: z.string().optional().describe('Filter tasks due before this date (ISO 8601)'),
  due_after: z.string().optional().describe('Filter tasks due after this date (ISO 8601)'),
  category_id: z.string().uuid().optional().describe('Filter by category ID'),
  limit: z.number().min(1).max(50).default(10).describe('Maximum number of results'),
  include_subtasks: z.boolean().default(false).describe('Include subtasks in results'),
});

export type SearchTasksToolInput = z.infer<typeof SearchTasksToolSchema>;

/**
 * Schema for getting user's tasks
 */
export const GetMyTasksToolSchema = z.object({
  status: z.enum(['todo', 'in_progress', 'completed', 'all']).default('all').describe('Filter by status'),
  timeframe: z.enum(['today', 'this_week', 'overdue', 'upcoming', 'all']).default('all').describe('Time-based filter'),
  limit: z.number().min(1).max(50).default(20).describe('Maximum number of results'),
});

export type GetMyTasksToolInput = z.infer<typeof GetMyTasksToolSchema>;

/**
 * Schema for updating a task
 */
export const UpdateTaskToolSchema = z.object({
  task_id: z.string().uuid().describe('The ID of the task to update'),
  title: z.string().min(1).max(500).optional().describe('New title for the task'),
  description: z.string().max(5000).optional().describe('New description'),
  status: z.enum(['todo', 'in_progress', 'completed']).optional().describe('New status'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).optional().describe('New priority'),
  due_date: z.string().optional().nullable().describe('New due date (ISO 8601) or null to remove'),
  assigned_to: z.string().uuid().optional().nullable().describe('New assignee ID or null to unassign'),
});

export type UpdateTaskToolInput = z.infer<typeof UpdateTaskToolSchema>;

/**
 * Schema for completing a task
 */
export const CompleteTaskToolSchema = z.object({
  task_id: z.string().uuid().describe('The ID of the task to complete'),
  completion_notes: z.string().max(1000).optional().describe('Notes about the completion'),
});

export type CompleteTaskToolInput = z.infer<typeof CompleteTaskToolSchema>;

// =============================================================================
// CALENDAR TOOL SCHEMAS
// =============================================================================

/**
 * Schema for creating a calendar event
 */
export const CreateCalendarEventToolSchema = z.object({
  title: z.string().min(1).max(200).describe('Event title'),
  description: z.string().max(5000).optional().describe('Event description'),
  start_time: z.string().describe('Start time in ISO 8601 format'),
  end_time: z.string().describe('End time in ISO 8601 format'),
  location: z.string().max(500).optional().describe('Event location or meeting link'),
  attendees: z.array(z.string().email()).optional().describe('Email addresses of attendees'),
  reminder_minutes: z.number().min(0).max(10080).optional().describe('Reminder before event (minutes)'),
  is_all_day: z.boolean().default(false).describe('Whether this is an all-day event'),
});

export type CreateCalendarEventToolInput = z.infer<typeof CreateCalendarEventToolSchema>;

/**
 * Schema for getting calendar availability
 */
export const GetCalendarAvailabilityToolSchema = z.object({
  date: z.string().describe('Date to check availability (YYYY-MM-DD)'),
  duration_minutes: z.number().min(15).max(480).default(60).describe('Desired meeting duration'),
  start_hour: z.number().min(0).max(23).default(9).describe('Earliest hour to consider (24h format)'),
  end_hour: z.number().min(0).max(23).default(17).describe('Latest hour to consider (24h format)'),
});

export type GetCalendarAvailabilityToolInput = z.infer<typeof GetCalendarAvailabilityToolSchema>;

/**
 * Schema for getting calendar events
 */
export const GetCalendarEventsToolSchema = z.object({
  start_date: z.string().describe('Start of date range (YYYY-MM-DD)'),
  end_date: z.string().describe('End of date range (YYYY-MM-DD)'),
  limit: z.number().min(1).max(100).default(25).describe('Maximum events to return'),
});

export type GetCalendarEventsToolInput = z.infer<typeof GetCalendarEventsToolSchema>;

// =============================================================================
// USER TOOL SCHEMAS
// =============================================================================

/**
 * Schema for getting user info
 */
export const GetUserInfoToolSchema = z.object({
  include_stats: z.boolean().default(false).describe('Include task statistics'),
  include_recent_activity: z.boolean().default(false).describe('Include recent activity'),
});

export type GetUserInfoToolInput = z.infer<typeof GetUserInfoToolSchema>;

/**
 * Schema for getting team members
 */
export const GetTeamMembersToolSchema = z.object({
  include_workload: z.boolean().default(false).describe('Include current workload for each member'),
  role_filter: z.string().optional().describe('Filter by role'),
});

export type GetTeamMembersToolInput = z.infer<typeof GetTeamMembersToolSchema>;

// =============================================================================
// TOOL REGISTRY TYPES
// =============================================================================

/**
 * Available tools in the system
 */
export type ToolName =
  | 'create_task'
  | 'search_tasks'
  | 'get_my_tasks'
  | 'update_task'
  | 'complete_task'
  | 'create_calendar_event'
  | 'get_calendar_availability'
  | 'get_calendar_events'
  | 'get_user_info'
  | 'get_team_members';

/**
 * Tool definition for the registry
 */
export interface ToolDefinition<TInput = unknown, TOutput = unknown> {
  name: ToolName;
  description: string;
  category: 'tasks' | 'calendar' | 'users' | 'reports' | 'settings';
  schema: z.ZodSchema<TInput>;
  requiresAuth: boolean;
  requiredPermissions?: string[];
  execute: (input: TInput, context: ToolContext) => Promise<ToolResult<TOutput>>;
}

/**
 * Tool call request from AI
 */
export interface ToolCall {
  id: string;
  name: ToolName;
  arguments: Record<string, unknown>;
}

/**
 * Tool call response to AI
 */
export interface ToolCallResponse {
  id: string;
  name: ToolName;
  result: ToolResult;
}

// =============================================================================
// TOOL RESULT TYPES (Specific return types for each tool)
// =============================================================================

export interface CreateTaskResult {
  task_id: string;
  title: string;
  status: string;
  priority: string;
  due_date?: string;
  url: string;
}

export interface SearchTasksResult {
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    due_date?: string;
    assigned_to?: string;
    assignee_name?: string;
  }>;
  total_count: number;
  has_more: boolean;
}

export interface GetMyTasksResult {
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    due_date?: string;
    is_overdue: boolean;
  }>;
  summary: {
    total: number;
    todo: number;
    in_progress: number;
    completed: number;
    overdue: number;
  };
}

export interface CalendarEventResult {
  event_id: string;
  title: string;
  start_time: string;
  end_time: string;
  location?: string;
  calendar_url?: string;
}

export interface CalendarAvailabilityResult {
  date: string;
  available_slots: Array<{
    start_time: string;
    end_time: string;
    duration_minutes: number;
  }>;
  busy_slots: Array<{
    start_time: string;
    end_time: string;
    title?: string;
  }>;
}

export interface UserInfoResult {
  id: string;
  email: string;
  full_name?: string;
  role: string;
  stats?: {
    total_tasks: number;
    completed_tasks: number;
    pending_tasks: number;
    overdue_tasks: number;
  };
}

export interface TeamMembersResult {
  members: Array<{
    id: string;
    full_name?: string;
    email: string;
    role: string;
    workload?: {
      assigned_tasks: number;
      capacity: number;
    };
  }>;
  total_count: number;
}
